import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateAfterAssessmentStart } from 'core/api/invalidation';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { CompanyCodeSection } from 'features/assessment/CompanyCodeSection';
import { userApi } from 'features/home/home.api';
import { Button } from 'shared/components/Button';
import { Card, ErrorState, HeroBand, Skeleton } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';

export function AssessmentIntroPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const query = useQuery({
    queryKey: queryKeys.assessment,
    queryFn: () => userApi.assessment(),
  });

  const assessment = query.data?.assessment;
  const current = query.data?.current_attempt || query.data?.attempt;
  const questionCount = query.data?.total_questions
    || assessment?.active_question_count
    || assessment?.total_questions;

  async function startOrResume() {
    if (starting) {
      return;
    }
    setStarting(true);
    setError('');
    try {
      if (current?.id && current.status === 'in_progress') {
        navigate(paths.assessmentAttempt(current.id));
        return;
      }
      const data = await userApi.startAttempt();
      const id = data?.attempt?.id ?? data?.attempt_id;
      if (!id) {
        setError(t('assessment.unavailable'));
        return;
      }
      await invalidateAfterAssessmentStart(queryClient, id);
      navigate(paths.assessmentAttempt(id));
    } catch (err) {
      setError(humanizeApiError(err, t));
    } finally {
      setStarting(false);
    }
  }

  if (query.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={() => query.refetch()} retryLabel={t('actions.retry')} />;
  }

  return (
    <div className="intro-page">
      <PageMeta titleKey="meta.assessment" />
      <HeroBand
        tone="page"
        title={assessment?.title || t('assessment.introTitle')}
        body={t('assessment.introBody')}
      />
      {questionCount ? <p className="muted">{t('assessment.questionCount', { count: questionCount })}</p> : null}
      <div className="intro-paths">
        <Card className="intro-path intro-path-personal" data-testid="personal-assessment-section">
          <h2>{t('assessment.personalTitle')}</h2>
          <p className="muted">{t('assessment.personalBody')}</p>
          {assessment ? (
            <Button loading={starting} onClick={startOrResume}>
              {current?.status === 'in_progress' ? t('actions.resumeAssessment') : t('actions.startAssessment')}
            </Button>
          ) : (
            <p className="muted">{t('assessment.unavailable')}</p>
          )}
          {error ? <p className="field-error" role="alert">{error}</p> : null}
        </Card>
        <CompanyCodeSection />
      </div>
      <div className="intro-grid">
        <Card>
          <h2>{t('assessment.howTitle')}</h2>
          <p>{t('assessment.howBody')}</p>
        </Card>
        <Card>
          <h2>{t('assessment.privacyTitle')}</h2>
          <p>{t('assessment.privacyBody')}</p>
        </Card>
      </div>
      <Link className="text-link" to={paths.history}>{t('actions.viewHistory')}</Link>
    </div>
  );
}
